import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  MENTAL_SKILLS_SUBSCALES,
  MENTAL_SKILLS_SUBSCALE_MAX,
} from '../constants/mentalSkills';

// Title-case fallback for any key not in the map above.
const titleCase = (key) =>
  String(key)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

function SubscaleBar({ label, value, max }) {
  const safe = typeof value === 'number' ? value : 0;
  const pct = Math.max(0, Math.min(100, (safe / max) * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
      <div style={{ width: 170, color: '#5b6473' }}>{label}</div>
      <div style={{ flex: 1, background: 'rgba(20, 23, 31, 0.08)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: '#6e54b8', height: '100%', borderRadius: 999 }} />
      </div>
      <div style={{ width: 44, textAlign: 'right', fontWeight: 600, color: '#14171f' }}>
        {typeof value === 'number' ? `${value}/${max}` : '—'}
      </div>
    </div>
  );
}

/**
 * Read-only Mental Skills snapshot card (web→iOS parity).
 *
 * Fetches GET /api/athletes/{userId}/mental-skills/history (newest-first) and
 * renders the most recent submission: composite total/84 + a labeled bar per
 * subscale. This is display-only — assessments are taken in the mobile app.
 *
 * Props:
 *   userId — the athlete's user id whose history to fetch.
 */
export default function MentalSkillsCard({ userId }) {
  const { API_URL } = useAuth();
  const [latest, setLatest] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const getAxiosConfig = () => ({
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
  });

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoaded(true);
      return undefined;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/athletes/${userId}/mental-skills/history`,
          getAxiosConfig()
        );
        if (!cancelled) {
          const entries = Array.isArray(res.data) ? res.data : [];
          setLatest(entries.length > 0 ? entries[0] : null);
        }
      } catch (err) {
        // 404 / no history / access denied → empty state.
        if (!cancelled) setLatest(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, API_URL]);

  if (!loaded) return null;

  // ── Empty state ────────────────────────────────────────────────
  if (!latest || !latest.subscales) {
    return (
      <div className="ed-card">
        <h2 className="ed-card__title">Mental Skills</h2>
        <p className="ed-card__body">
          Take the Mental Skills assessment in the ProZpects app.
        </p>
      </div>
    );
  }

  const subscales = latest.subscales || {};
  // Known subscales in canonical order, then any extra keys the backend sends.
  const knownKeys = MENTAL_SKILLS_SUBSCALES.map((s) => s.key);
  const extraRows = Object.keys(subscales)
    .filter((k) => !knownKeys.includes(k))
    .map((k) => ({ key: k, label: titleCase(k) }));
  const rows = [...MENTAL_SKILLS_SUBSCALES, ...extraRows];

  return (
    <div className="ed-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <h2 className="ed-card__title">Mental Skills</h2>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#14171f', lineHeight: 1 }}>
            {latest.total}
            <span style={{ fontSize: 16, fontWeight: 600, color: '#5b6473' }}>/84</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((s) => (
          <SubscaleBar key={s.key} label={s.label} value={subscales[s.key]} max={MENTAL_SKILLS_SUBSCALE_MAX} />
        ))}
      </div>

      {latest.completed_at && (
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          Updated {new Date(latest.completed_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}
